import {Axios} from '@galilee/sdk-core';
import {getAuthorizationJwtHeader} from '@common/helpers/api/apiHelper';
import {StsCredentials} from '@common/types/aws/aws.types';
import {ActionResponse} from '@common/types/documents/apiResponses.types';
import {CONTENT_TYPE_JSON} from '@common/constants/api/apiConstants';
import {SetClaimIntentionScoringFormValues, SetClaimScoringFormValues} from '@common/types/forms/productForms.types';

export const apiEditSection = async ({
  additionalBodyParams = {},
  baseUrl,
  uuid,
  credentials,
  scopedJwt,
  action,
  data
}: {
  additionalBodyParams?: Record<string, string | number>;
  baseUrl: string;
  uuid: string;
  credentials: StsCredentials;
  scopedJwt: string;
  action: string;
  data: object;
}): Promise<ActionResponse> => {
  return new Axios<object, ActionResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', action)
    .post({
      ...data,
      ...additionalBodyParams
    });
};

export const apiScoringDone = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimId: string}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'set-scoring-done')
    .post(data);
};

export const apiApproveReview = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimId: string; comment?: string}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'set-review-approve')
    .post(data);
};

export const apiRejectReview = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimId: string; comment: string}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'set-review-reject')
    .post(data);
};

export const apiPutClaimInDraft = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimIds: string[]}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'put-claim-in-draft')
    .post(data);
};

export const apiAskForRewiew = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimIds: string[]}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'ask-for-review')
    .post(data);
};

export const apiSetClaimScoring = async (
  baseUrl: string,
  uuid: string,
  credentials: StsCredentials,
  scopedJwt: string,
  data: SetClaimScoringFormValues | SetClaimIntentionScoringFormValues
): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'set-claim-scoring')
    .post(data);
};

export const apiAskForScoring = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimIds: string[]}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'ask-for-scoring')
    .post(data);
};

export const apiBypassScoring = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {claimIds: string[]}): Promise<{transactionId: string}> => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'bypass-claim-scoring')
    .post(data);
};

export const apiDuplicateGmc = async (
  baseUrl: string,
  fromGmcUuid: string,
  data: object,
  credentials: StsCredentials,
  scopedJwt: string
): Promise<{data: {gmcId: string; gmcBucketId: string}; transactionId: string}> => {
  return new Axios<object, {data: {gmcId: string; gmcBucketId: string}; transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', fromGmcUuid, 'actions', 'duplicate')
    .post(data);
};
